import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { profileService } from "services/profileService";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import { tagStatusText, toMessage, formatLocations } from "./utils";
import { toast } from "react-toastify";
import JobOpportunityDrawer from "./JobOpportunityDrawer";

const truncate = (value, maxLength = 40) => {
  const text = value || "-";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export default function JobOpportunitiesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [options, setOptions] = useState({
    skills: [],
    specializations: [],
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const loadOptions = async () => {
    const [skillsRes, specializationsRes] = await Promise.all([
      olreadiaService.professionalSkills.list(),
      olreadiaService.professionalSpecializations.list(),
    ]);

    setOptions({
      skills: Array.isArray(skillsRes) ? skillsRes : [],
      specializations: Array.isArray(specializationsRes)
        ? specializationsRes
        : [],
    });
  };

  const fetchPage = useCallback(async (pag = pagination) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pag.current - 1,
        size: pag.pageSize,
        ...(pag.search ? { search: pag.search } : {}),
      };
      const res = await (pag.search
        ? olreadiaService.jobOpportunities.search(params)
        : olreadiaService.jobOpportunities.list(params));
      const content = Array.isArray(res?.content) ? res.content : [];
      setItems(content);
      setPagination((prev) => ({
        ...prev,
        current: pag.current,
        pageSize: pag.pageSize,
        total: res?.totalElements ?? content.length,
      }));
    } catch (err) {
      setError(toMessage(err, "Failed to load job opportunities"));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([fetchPage(), loadOptions()]);
      } catch (err) {
        setError(toMessage(err, "Failed to load job opportunities"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        fetchPage({ current: 1, pageSize: pagination.pageSize, search: query.trim() });
      } else {
        fetchPage({ current: 1, pageSize: pagination.pageSize });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, pagination.pageSize, fetchPage]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((item) => {
  const jobLocations = formatLocations(item?.requiredLocations) || "";
      const workSchedules = Array.isArray(item?.workSchedules) && item.workSchedules.length
        ? item.workSchedules.join(", ").replace(/_/g, " ")
        : "";
      return [
        item?.title,
        item?.description,
        item?.status,
        workSchedules,
        jobLocations,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [items, query]);

  const handleDelete = (record) => {
    setRecordToDelete(record);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    setDeleting(true);
    try {
      await olreadiaService.jobOpportunities.delete(recordToDelete.id, actorId);
      toast.success("Job opportunity removed");
      await fetchPage();
    } catch (err) {
      toast.error(toMessage(err, "Failed to delete job opportunity"));
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await olreadiaService.jobOpportunities.update(
          editing.id,
          values,
          actorId,
        );
      } else {
        await olreadiaService.jobOpportunities.create(values, actorId);
      }
      setDrawerOpen(false);
      await fetchPage();
      toast.success(editing?.id ? "Job opportunity updated" : "Job opportunity created");
    } catch (err) {
      toast.error(toMessage(err, "Failed to save job opportunity"));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value || "-"} placement="topLeft">
          <span style={{ fontWeight: 600 }}>{truncate(value, 40)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (value) => <Tag color="green">{tagStatusText(value)}</Tag>,
    },
    {
      title: "Job Type",
      dataIndex: "workSchedules",
      key: "workSchedules",
      width: 140,
      render: (value) => {
        const text = Array.isArray(value) && value.length ? value[0].replace(/_/g, " ") : "-";
        return <Tag color="blue">{text}</Tag>;
      },
    },
    {
      title: "Locations",
      key: "locations",
      ellipsis: true,
      render: (_, record) => {
        const text = formatLocations(record?.requiredLocations) || "-";
        return (
          <Tooltip title={text} placement="topLeft">
            <span>{truncate(text, 20)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Posted",
      key: "dates",
      width: 200,
      render: (_, record) => {
        const start = record?.postStartDate || "";
        const end = record?.postEndDate || "";
        const text = start && end ? `${start.split("T")[0]} → ${end.split("T")[0]}` : start || end || "-";
        return (
          <Tooltip title={text} placement="topLeft">
            <span>{truncate(text, 30)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="prof-jobs-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "view",
                  label: "View",
                  icon: <EyeOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    navigate(paths.jobOpportunityDetails(record.id), {
                      state: { jobOpportunity: record },
                    });
                  },
                },
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditing(record);
                    setDrawerOpen(true);
                  },
                },
                {
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleDelete(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="briefcase"
          title="Job Opportunities"
          subtitle="Create and manage job postings."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchPage()}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            New Job
          </Button>
        </Space>
      </div>

      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: 8 }}
      >
        <Input.Search
          allowClear
          placeholder="Search by title, type, location, or status"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", maxWidth: 320 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => fetchPage()}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredItems}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={(pag) =>
          fetchPage({ current: pag.current, pageSize: pag.pageSize })
        }
        locale={{ emptyText: "No job opportunities found." }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".prof-jobs-actions")) return;
            navigate(paths.jobOpportunityDetails(record.id), {
              state: { jobOpportunity: record },
            });
          },
        })}
      />

      <JobOpportunityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={saving}
        initialValues={editing}
        skills={options.skills}
        specializations={options.specializations}
      />

      <Modal
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        title="Remove job opportunity?"
        okText="Remove"
        okButtonProps={{ danger: true, loading: deleting }}
        onOk={confirmDelete}
        zIndex={2000}
      >
        <p>This will permanently delete the job opportunity.</p>
      </Modal>
    </div>
  );
}
